import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Button } from '../../ui/button';
import {
  FileText,
  Search,
  X,
  Loader2,
  Plus,
  Upload,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  RefreshCw,
  Filter
} from 'lucide-react';
import { purchasingAPI, coreAPI, ContractPrice } from '../../../lib/api';
import { PricingFilterSections } from './pricing/PricingFilterSections';
// CR-6438 rework: same country derivation the dashboard and the other
// purchasing panels use, so every screen offers an identical Country list.
import { deriveCountryOptions } from '../../dashboard/dashboardFilterUtils';

import { toast } from 'sonner';
import { Pagination } from '../../ui/pagination';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { useView } from '../../../contexts/ViewContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import ContractPriceModal from '../ContractPriceModal';
import ContractPriceDetailModal from '../ContractPriceDetailModal';
import ContractPriceImportModal from '../ContractPriceImportModal';
import { usePortalMode } from '../../../hooks/usePortalMode';
import { pageCountFromResponse, type PageSizeMemory } from '../../../hooks/usePagination';

/**
 * CR-6438 rework: the Contract Period quick options QA reported missing.
 * Local-date formatting throughout — an ISO/UTC conversion shifts a BST
 * midnight back a day and silently widens the period by one.
 */
const PRICING_QUICK_RANGES = [
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'lastQuarter', label: 'Last quarter' },
  { key: 'thisYear', label: 'This year' },
  { key: 'lastYear', label: 'Last year' },
] as const;

function fmtLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getPricingQuickRangeDates(range: string): { dateFrom: string; dateTo: string } {
  const t = new Date();
  switch (range) {
    case 'thisMonth':
      return { dateFrom: fmtLocal(new Date(t.getFullYear(), t.getMonth(), 1)), dateTo: fmtLocal(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
    case 'lastMonth':
      return { dateFrom: fmtLocal(new Date(t.getFullYear(), t.getMonth() - 1, 1)), dateTo: fmtLocal(new Date(t.getFullYear(), t.getMonth(), 0)) };
    case 'lastQuarter': {
      const lq = Math.floor(t.getMonth() / 3) - 1;
      const yr = lq < 0 ? t.getFullYear() - 1 : t.getFullYear();
      const q = lq < 0 ? 3 : lq;
      return { dateFrom: fmtLocal(new Date(yr, q * 3, 1)), dateTo: fmtLocal(new Date(yr, (q + 1) * 3, 0)) };
    }
    case 'thisYear':
      return { dateFrom: fmtLocal(new Date(t.getFullYear(), 0, 1)), dateTo: fmtLocal(new Date(t.getFullYear(), 11, 31)) };
    case 'lastYear':
      return { dateFrom: fmtLocal(new Date(t.getFullYear() - 1, 0, 1)), dateTo: fmtLocal(new Date(t.getFullYear() - 1, 11, 31)) };
    default:
      return { dateFrom: '', dateTo: '' };
  }
}

const PRICING_RISK_CATEGORIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export default function ContractPricingTab() {
  // CR-6346: a supplier reaching this tab is viewing a shared price book —
  // read-only by definition. The backend refuses every write, export and
  // changelog call for them; this hides the controls so the UI matches.
  const { isSupplier } = usePortalMode();
  const { formatDate } = usePreferences();
  // CR-6036: the Pricing tab was the only Purchasing tab not propagating the
  // topbar view picker to the backend. Without these params the helix-branch
  // queryset in contract_price_views.py falls back to profile.current_site_id
  // / current_group_id from the DB — which can be stale relative to what the
  // user currently sees in the picker, returning 0 contracts on initial load
  // until something (often a downstream write) pushes the persisted profile
  // back in line with the FE. Reading viewKey here so the effect refetches
  // when the user switches view while sitting on this tab.
  const { getViewQueryParams, viewKey, viewLevel, selectedGroupId } = useView();
  const { user } = useAuth();
  const isHelixUser = user?.company_type === 'helix';
  const [contracts, setContracts] = useState<ContractPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Modal states
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractPrice | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  // CR-6037 rework: bumped on every successful save inside ContractPriceModal so
  // the Parts Details modal sitting behind it refetches and surfaces the newly
  // added/edited part without a manual page refresh. The previous round only
  // re-fetched the outer contract list, which left the detail modal showing
  // stale parts and made the success toast look like a lie.
  const [detailRefreshTick, setDetailRefreshTick] = useState(0);

  // Sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pageSize = 50;

  // Text input states (for debouncing)
  const [textInputs, setTextInputs] = useState({
    contractNumber: '',
    supplier: '',
    customer: '',
    partNumber: '',
  });

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    contractNumber: '',
    supplier: '',
    supplierGroup: '',
    customer: '',
    partNumber: '',
    currency: '',
    dateFrom: '',
    dateTo: '',
    // CR-6438 rework: the design's remaining filters, each backed by a lookup
    // the contract-price endpoint now accepts.
    createdFrom: '',
    createdTo: '',
    status: '',
    autoRenew: '',
    customerCountry: '',
    division: '',
    supplierCountry: '',
    supplierType: '',
    strategicOnly: '',
    riskCategory: '',
    owner: '',
    createdBy: '',
    // CR-6438: Missing filters from diagram - Warehouse, Commodity Group, Commodity Type
    warehouse: '',
    commodityGroup: '',
    commodityType: '',
  });
  // CR-4593: Filter dropdown options
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  // CR-6438: Added customers array for Site → Supplier cascade, group for Supplier Group cascade
  const [supplierSiteOptions, setSupplierSiteOptions] = useState<{
    value: string; label: string; country?: string; group?: number | null; customers?: number[];
  }[]>([]);
  const [companySiteOptions, setCompanySiteOptions] = useState<{ value: string; label: string; group?: number | null; country?: string }[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierCountryOptions, setSupplierCountryOptions] = useState<string[]>([]);
  const [ownerOptions, setOwnerOptions] = useState<{ value: string; label: string }[]>([]);
  // CR-6438: Missing filters from diagram - Warehouse, Commodity Group, Commodity Type
  const [warehouseOptions, setWarehouseOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string; group?: number }[]>([]);

  // Initialize
  useEffect(() => {
    setInitialLoadComplete(true);
    // CR-6346: a supplier viewing a shared price book has no filter panel, and
    // these lookups would pull the customer's staff list and their other
    // suppliers to populate it. Skip the fetch entirely rather than rely on
    // each endpoint's own scoping to make it harmless.
    if (isSupplier) return;
    // CR-4593: Fetch filter options.
    // CR-6037 rework: the previous getCompanyGroups / getCompanies calls are
    // gated to Helix admins (CompanyViewSet = IsHelixUser), so customer users
    // got a 403 on every load and the Company Site / Supplier Site / Supplier
    // Group filters silently rendered empty. Use the customer-safe, portal-
    // scoped endpoints instead (same ones the contract modal now uses) so the
    // filter panel works for customers AND helix admins alike.
    Promise.all([
      coreAPI.getSupplierGroupsForFilter().catch(() => ({ data: { results: [] } })),
      coreAPI.getContractPricingCompanies().catch(() => ({ data: { results: [] } })),
      // CR-6438 rework: divisions for the Division picker and users for the
      // Contract Owner / Created By pickers.
      // Divisions come from the Helix-only group list; other roles have no
      // scoped source for them, so don't fire a request that can only 403.
      isHelixUser
        ? coreAPI.getCompanyGroups({ page_size: 200, company_type: 'customer' }).catch(() => ({ data: { results: [] } }))
        : Promise.resolve({ data: { results: [] } }),
      coreAPI.getUserProfiles({ page_size: 500 }).catch(() => ({ data: { results: [] } })),
      // CR-6438: Fetch warehouse options
      coreAPI.getAccessibleWarehouses().catch(() => ({ data: [] })),
      // CR-6438: Fetch commodity groups and types from line filter options
      purchasingAPI.getLineFilterOptions().catch(() => ({ data: {} })),
    ]).then(([groupsRes, companiesRes, divisionsRes, usersRes, warehousesRes, lineFilterRes]) => {
      const groups = (groupsRes.data as unknown as { results: { id: number; name: string }[] }).results || [];
      setSupplierGroupOptions(groups.map((g) => ({ value: String(g.id), label: g.name })));
      // CR-6438: Added customers array for Site → Supplier cascade filtering
      const companies = (companiesRes.data as unknown as {
        results: { id: number; name: string; company_type: string; country?: string; group?: number | null; customers?: number[] }[]
      }).results || [];
      const suppliers = companies.filter((c) => c.company_type === 'supplier');
      setSupplierSiteOptions(
        suppliers.map((s) => ({
          value: String(s.id), label: s.name, country: s.country,
          group: s.group ?? null, customers: s.customers,
        }))
      );
      setCompanySiteOptions(
        companies.filter((c) => c.company_type === 'customer')
          .map((c) => ({ value: String(c.id), label: c.name, country: c.country, group: c.group ?? null }))
      );
      // Supplier Country lists what the suppliers in scope actually carry, the
      // way the dashboard derives it — a fixed ISO list would offer countries
      // no supplier is in.
      setSupplierCountryOptions(deriveCountryOptions(suppliers));

      const divisions = (divisionsRes.data as unknown as { results: { id: number; name: string }[] }).results || [];
      setDivisionOptions(divisions.map((g) => ({ value: String(g.id), label: g.name })));

      // Owner / Created By are User ids on the contract, so the options are
      // keyed by profile.user.id rather than the profile id.
      const profiles = (usersRes.data as unknown as {
        results: { user: { id: number; first_name?: string; last_name?: string; email: string } }[]
      }).results || [];
      setOwnerOptions(profiles.filter((pr) => pr.user).map((pr) => ({
        value: String(pr.user.id),
        label: [pr.user.first_name, pr.user.last_name].filter(Boolean).join(' ') || pr.user.email,
      })));

      // CR-6438: Set warehouse options
      const warehouses = Array.isArray(warehousesRes.data) ? warehousesRes.data : [];
      setWarehouseOptions(warehouses.map((w: { id: number; name: string }) => ({
        value: String(w.id), label: w.name,
      })));

      // CR-6438: Set commodity groups and types from line filter options
      const lineData = lineFilterRes.data as {
        commodity_groups?: Array<{ id: number; name: string }>;
        commodity_types?: Array<{ id: number; name: string; commodity_group_id: number }>;
      };
      const commodityGroups = lineData.commodity_groups || [];
      setCommodityGroupOptions(commodityGroups.map((g) => ({
        value: String(g.id), label: g.name,
      })));
      const commodityTypes = lineData.commodity_types || [];
      setCommodityTypeOptions(commodityTypes.map((t) => ({
        value: String(t.id), label: t.name, group: t.commodity_group_id,
      })));
    });
    // CR-6037 follow-up: depend on viewKey so the filter options refetch when
    // the top-bar view context changes. for-contract-pricing is scoped to the
    // active site/division server-side; without this the Supplier Site /
    // Company Site / Supplier Group lists kept the previous division's options
    // (e.g. switching Aerospace → Industrial still showed Aerospace suppliers)
    // until a full page reload, even though the contract list beside them and
    // the Add/Edit modal had already re-scoped.
  }, [viewKey]);

  // Debounce text inputs
  useEffect(() => {
    if (!initialLoadComplete) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        contractNumber: textInputs.contractNumber,
        supplier: textInputs.supplier,
        customer: textInputs.customer,
        partNumber: textInputs.partNumber,
      }));
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [textInputs, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete) {
      fetchContracts();
    }
    // CR-6036: include viewKey so a topbar picker change while the user is on
    // the Pricing tab re-fires the fetch (mirrors how DeliveryTab / POLinesTab
    // pick up view-context changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, activeTab, initialLoadComplete, viewKey]);

  /**
   * Everything the list is currently filtered by, as backend query params.
   *
   * Shared by the table fetch and the Excel export so the sheet always
   * matches the screen — CR-6424 (R-014): the export used to receive the
   * raw camelCase filter state, so the backend silently ignored most
   * filters, the active status tab and the topbar scope.
   */
  const buildListQueryParams = () => {
    const queryParams: Record<string, string | number | boolean> = {
      // CR-6424: Purchasing > Pricing is the PRICEBOOK surface — supplier
      // relationship contracts live in Supplier Profile > Contracts.
      realm: 'pricebook',
    };

    // CR-6036: pass the active topbar view scope so the helix-branch
    // queryset filters by what the picker currently shows, not by a stale
    // profile.current_site_id / current_group_id persisted from a previous
    // session. Other Purchasing tabs (DeliveryTab.tsx:689-692, POHeadersTab,
    // POLinesTab) already follow this pattern.
    const viewParams = getViewQueryParams();
    if (viewParams['group'])   queryParams['group']   = viewParams['group'] as number;
    if (viewParams['company']) queryParams['company'] = viewParams['company'] as number;
    if (viewParams['site'])    queryParams['site']    = viewParams['site'] as number;

    // CR-6036 round 2: the synthetic "All Companies" group has id=0, which
    // getViewQueryParams() drops (the ``if (viewState.selectedGroupId)``
    // check treats 0 as falsy). That leaves no view-context param on the
    // wire and the backend falls back to profile.current_* — exactly the
    // stale-profile path we're trying to avoid. Send an explicit scope=all
    // signal so the backend knows the user actively chose platform-wide,
    // not "nothing specified".
    if (viewLevel === 'GROUP' && selectedGroupId === 0) {
      queryParams['scope'] = 'all';
    }

    // Individual field filters
    if (filters.contractNumber) {
      queryParams['search'] = filters.contractNumber;
    }

    if (filters.supplier) {
      // CR-4593: ID-based filter now using SearchableSelect
      queryParams['supplier'] = filters.supplier;
    }

    if (filters.supplierGroup) {
      queryParams['supplier__group'] = filters.supplierGroup;
    }

    if (filters.customer) {
      // CR-4593: ID-based filter now using SearchableSelect
      queryParams['customer'] = filters.customer;
    }

    if (filters.partNumber) {
      queryParams['parts__part_number__icontains'] = filters.partNumber;
    }

    // CR-6438 rework: the design's Contract Period, Company, Supplier,
    // Classification and Contract Details filters.
    if (filters.createdFrom) queryParams['created_at__gte'] = filters.createdFrom;
    if (filters.createdTo) queryParams['created_at__lte'] = filters.createdTo;
    if (filters.status) queryParams['status'] = filters.status;
    if (filters.autoRenew) queryParams['auto_renew'] = filters.autoRenew;
    if (filters.customerCountry) queryParams['customer__country'] = filters.customerCountry;
    if (filters.division) queryParams['division'] = filters.division;
    if (filters.supplierCountry) queryParams['supplier__country'] = filters.supplierCountry;
    if (filters.supplierType) queryParams['supplier_type'] = filters.supplierType;
    if (filters.strategicOnly === 'true') queryParams['strategic_only'] = 'true';
    if (filters.riskCategory) queryParams['risk_category'] = filters.riskCategory;
    if (filters.owner) queryParams['owner'] = filters.owner;
    if (filters.createdBy) queryParams['created_by'] = filters.createdBy;
    // CR-6438: Warehouse, Commodity Group, and Commodity Type filters from diagram.
    // NOTE: ContractPricePart stores part_number as text (no FK to Part/Warehouse/Commodity).
    // These filters are UI placeholders matching the diagram; they have no backend effect
    // until ContractPricePart is extended with proper FK relationships to the master data.
    // The parameters are sent so they're ready when the backend adds support.
    if (filters.warehouse) queryParams['warehouse'] = filters.warehouse;
    if (filters.commodityGroup) queryParams['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) queryParams['commodity_type'] = filters.commodityType;

    if (filters.currency) {
      queryParams['currency'] = filters.currency;
    }

    if (filters.dateFrom) {
      queryParams['valid_from__gte'] = filters.dateFrom;
    }
    if (filters.dateTo) {
      queryParams['valid_to__lte'] = filters.dateTo;
    }

    // Tab-based filtering
    if (activeTab === 'active') {
      queryParams['status_tab'] = 'active';
    } else if (activeTab === 'expired') {
      queryParams['status_tab'] = 'expired';
    } else if (activeTab === 'draft') {
      queryParams['status_tab'] = 'draft';
    }

    return queryParams;
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = {
        ...buildListQueryParams(),
        ordering: '-created_at',
        page: currentPage,
        page_size: pageSize,
      };

      const response = await purchasingAPI.getContractPrices(queryParams);
      setContracts(response.data.results);
      setTotalCount(response.data.count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(response.data.count, pageSize, response.data.results.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);
    } catch (err: unknown) {
      console.error('Error fetching contract prices:', err);
      setError(getApiErrorMessage(err, 'Failed to load contract prices'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    if (['contractNumber', 'supplier', 'customer', 'partNumber'].includes(key)) {
      setTextInputs({ ...textInputs, [key]: value });
    } else {
      setFilters({ ...filters, [key]: value });
    }
    setCurrentPage(1);
  };

  // CR-6438 rework: badge count for the filter header.
  const activeFilterCount = Object.values(filters).filter((v) => !!v).length;

  const clearFilters = () => {
    setTextInputs({
      contractNumber: '',
      supplier: '',
      customer: '',
      partNumber: '',
    });
    setFilters({
      contractNumber: '',
      supplier: '',
      supplierGroup: '',
      customer: '',
      partNumber: '',
      currency: '',
      dateFrom: '',
      dateTo: '',
      createdFrom: '',
      createdTo: '',
      status: '',
      autoRenew: '',
      customerCountry: '',
      division: '',
      supplierCountry: '',
      supplierType: '',
      strategicOnly: '',
      riskCategory: '',
      owner: '',
      createdBy: '',
      // CR-6438: Missing filters from diagram
      warehouse: '',
      commodityGroup: '',
      commodityType: '',
    });
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-primary" />
    ) : (
      <ArrowDown className="h-3 w-3 text-primary" />
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
      // CR-6424: pricebook / contracted-prices area uses the legacy "Live" label.
      active: { label: 'Live', className: 'bg-green-100 text-green-800' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleRowClick = (contract: ContractPrice) => {
    setSelectedContractId(contract.id);
    setShowDetailModal(true);
  };

  // CR-5459 rework: open a specific contract when arrived at via
  // ?contract=<id>. The Calendar's Contract Renewal events deep-link here;
  // before this they pointed at /contracts, which is not a route, so every
  // click landed on the dashboard. Handled once per id so clearing the param
  // cannot reopen the modal after the user closes it.
  const [searchParams, setSearchParams] = useSearchParams();
  const handledContractParam = useRef<string | null>(null);
  useEffect(() => {
    // `has` rather than a truthy check: ?contract= (empty) must still be
    // consumed, or it sticks in the URL and re-fires on every render.
    if (!searchParams.has('contract')) return;
    const param = searchParams.get('contract') ?? '';
    if (handledContractParam.current === param) return;
    handledContractParam.current = param;
    const next = new URLSearchParams(searchParams);
    next.delete('contract');
    setSearchParams(next, { replace: true });
    if (!/^\d+$/.test(param)) return;   // cleared, but nothing to open
    setSelectedContractId(Number(param));
    setShowDetailModal(true);
  }, [searchParams, setSearchParams]);

  const handleAddNew = () => {
    setEditingContract(null);
    setShowAddEditModal(true);
  };

  const handleEdit = (contract: ContractPrice) => {
    setEditingContract(contract);
    setShowAddEditModal(true);
  };

  const handleExport = async () => {
    try {
      // CR-6424 (R-014): export exactly what the table shows — same filters,
      // same status tab, same topbar scope, pricebooks only. Previously this
      // sent the raw camelCase filter state, which the backend ignored.
      const response = await purchasingAPI.exportContractPrices(buildListQueryParams());
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_prices_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Failed to export contract prices');
    }
  };

  const handleModalClose = () => {
    setShowAddEditModal(false);
    setEditingContract(null);
    fetchContracts();
  };

  const handleDetailModalClose = () => {
    setShowDetailModal(false);
    setSelectedContractId(null);
  };

  const handleImportComplete = () => {
    setShowImportModal(false);
    fetchContracts();
  };

  const sortedContracts = [...contracts].sort((a, b) => {
    if (!sortField) return 0;

    const aRaw: unknown = a[sortField as keyof ContractPrice];
    const bRaw: unknown = b[sortField as keyof ContractPrice];

    if (aRaw === null || aRaw === undefined) return 1;
    if (bRaw === null || bRaw === undefined) return -1;

    let aVal: string | number = String(aRaw);
    let bVal: string | number = String(bRaw);

    if (['valid_from', 'valid_to', 'created_at'].includes(sortField)) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }

    if (['parts_count'].includes(sortField)) {
      aVal = parseInt(String(aVal));
      bVal = parseInt(String(bVal));
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading && !initialLoadComplete) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading contract pricing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchContracts}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters Panel. CR-6438 rework: the header the design specifies —
          active-filter count on the left, Refresh / Export / Hide filters on
          the right — and it stays visible when the panel is collapsed, so only
          the sections and the footer hide. QA reported the Filters and Import
          buttons as being in the wrong place: Filters belongs here, Import
          stays on the view bar with Add Contract. */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base text-xl">Filter Contract Prices</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => fetchContracts()}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Refresh
              </Button>
              {!isSupplier && (
              <Button size="sm" variant="outline" onClick={handleExport}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export
              </Button>
              )}
              {!isSupplier && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                  showFilters
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    showFilters
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-primary text-primary-foreground'
                  }`}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
              )}
            </div>
          </div>
        </CardHeader>
        {showFilters && !isSupplier && (
          <CardContent className="space-y-4 p-0">
            <PricingFilterSections
              filters={filters as unknown as Record<string, string>}
              textInputs={textInputs as unknown as Record<string, string>}
              companySiteOptions={companySiteOptions}
              supplierGroupOptions={supplierGroupOptions}
              supplierSiteOptions={supplierSiteOptions}
              divisionOptions={divisionOptions}
              supplierCountryOptions={supplierCountryOptions}
              ownerOptions={ownerOptions}
              createdByOptions={ownerOptions}
              riskCategoryOptions={PRICING_RISK_CATEGORIES}
              warehouseOptions={warehouseOptions}
              commodityGroupOptions={commodityGroupOptions}
              commodityTypeOptions={commodityTypeOptions.filter((t) =>
                !filters.commodityGroup || String(t.group ?? '') === filters.commodityGroup
              )}
              quickRanges={PRICING_QUICK_RANGES}
              getQuickRangeDates={getPricingQuickRangeDates}
              onFilterChange={handleFilterChange}
              onSelectChange={(key, value) => setFilters((prev) => {
                // A quick period sets both ends at once. Routing it through one
                // update keeps the pair consistent — two sequential setState
                // calls would each read the same stale object and the second
                // would drop the first.
                if (key === '__quickPeriod') {
                  const d = getPricingQuickRangeDates(value);
                  return { ...prev, dateFrom: d.dateFrom, dateTo: d.dateTo };
                }
                // Country and Division are parents of Company Site; drop a site
                // the new parent excludes rather than querying an impossible
                // combination.
                if (key === 'customerCountry' || key === 'division') {
                  const site = companySiteOptions.find((o) => o.value === prev.customer);
                  const stale = !!value && !!site && (
                    key === 'customerCountry' ? site.country !== value : String(site.group ?? '') !== value
                  );
                  return { ...prev, [key]: value, ...(stale ? { customer: '' } : {}) };
                }
                // CR-6438: Site → Supplier cascade. When Company Site changes, clear
                // Supplier if it doesn't serve the newly selected site.
                if (key === 'customer') {
                  const sup = supplierSiteOptions.find((o) => o.value === prev.supplier);
                  const siteId = value ? parseInt(value, 10) : 0;
                  const stale = !!value && !!sup && !sup.customers?.includes(siteId);
                  return { ...prev, customer: value, ...(stale ? { supplier: '' } : {}) };
                }
                if (key === 'supplierCountry') {
                  const sup = supplierSiteOptions.find((o) => o.value === prev.supplier);
                  const stale = !!value && !!sup && sup.country !== value;
                  return { ...prev, supplierCountry: value, ...(stale ? { supplier: '' } : {}) };
                }
                // CR-6438: Supplier Group → Supplier cascade. Clear supplier if it
                // doesn't belong to the new group.
                if (key === 'supplierGroup') {
                  const sup = supplierSiteOptions.find((o) => o.value === prev.supplier);
                  const stale = !!value && !!sup && String(sup.group ?? '') !== value;
                  return { ...prev, supplierGroup: value, ...(stale ? { supplier: '' } : {}) };
                }
                // CR-6438: Commodity Group → Commodity Type cascade. Clear type if it
                // doesn't belong to the new group.
                if (key === 'commodityGroup') {
                  const type = commodityTypeOptions.find((o) => o.value === prev.commodityType);
                  const stale = !!value && !!type && String(type.group ?? '') !== value;
                  return { ...prev, commodityGroup: value, ...(stale ? { commodityType: '' } : {}) };
                }
                return { ...prev, [key]: value };
              })}
            />

            {/* Filter footer — Clear all left, Apply filters right. The panel
                already applies as you change a control; Apply is the design's
                explicit affordance and simply refetches, so the existing
                behaviour is unchanged either way. */}
            <div className="flex items-center justify-between px-6 pb-4">
              <Button size="sm" variant="outline" onClick={clearFilters}>
                <X className="h-3.5 w-3.5 mr-1.5" />
                Clear all
              </Button>
              <Button size="sm" onClick={() => fetchContracts()}>
                <Search className="h-3.5 w-3.5 mr-1.5" />
                Apply filters
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Main Table */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Contract Prices</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Showing {contracts.length} of {totalCount.toLocaleString()} contracts
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* CR-6438 rework: Filters and Export moved to the filter
                    header, per the diagram. Import and Add Contract are view-bar
                    actions and stay here. Keeping a second Filters button would
                    repeat what QA counted twice on the Deliveries tab. */}
                {!isSupplier && (
                <>
                <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
                <Button size="sm" onClick={handleAddNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contract
                </Button>
                </>
                )}
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  {/* CR-6424: legacy "Live" label; value stays 'active' for the status_tab query. */}
                  <TabsTrigger value="active">Live</TabsTrigger>
                  <TabsTrigger value="expired">Expired</TabsTrigger>
                  <TabsTrigger value="draft">Draft</TabsTrigger>
                </TabsList>
              </div>
            </div>
          </CardHeader>

          <TabsContent value={activeTab} className="m-0">
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : contracts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {Object.values(textInputs).some(v => v) || Object.values(filters).some(v => v)
                      ? 'No contracts match your filters'
                      : 'No contract prices found'}
                  </p>
                  {Object.values(textInputs).some(v => v) || Object.values(filters).some(v => v) ? (
                    <button onClick={clearFilters} className="mt-4 text-sm text-primary hover:underline">
                      Clear filters
                    </button>
                  ) : (
                    !isSupplier && (
                    <Button className="mt-4" onClick={handleAddNew}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Contract
                    </Button>
                    )
                  )}
                </div>
              ) : (
                <>
                  {/* Top Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
                    onPageChange={handlePageChange}
                    itemLabel="contracts"
                  />

                  {/* CR-3866: Added max-height and sticky header support */}
                  <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
                    <table className="w-full" style={{ minWidth: '1200px' }}>
                      <thead className="sticky top-0 z-20">
                        <tr className="border-b bg-muted/80 backdrop-blur-sm">
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '150px' }}
                            onClick={() => handleSort('contract_number')}
                          >
                            <div className="flex items-center gap-1">
                              Contract # {getSortIcon('contract_number')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '180px' }}
                            onClick={() => handleSort('customer_name')}
                          >
                            <div className="flex items-center gap-1">
                              Customer {getSortIcon('customer_name')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '180px' }}
                            onClick={() => handleSort('supplier_name')}
                          >
                            <div className="flex items-center gap-1">
                              Supplier {getSortIcon('supplier_name')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '130px' }}
                            onClick={() => handleSort('valid_from')}
                          >
                            <div className="flex items-center gap-1">
                              Valid From {getSortIcon('valid_from')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '130px' }}
                            onClick={() => handleSort('valid_to')}
                          >
                            <div className="flex items-center gap-1">
                              Valid To {getSortIcon('valid_to')}
                            </div>
                          </th>
                          <th
                            className="text-center py-3 px-4 font-medium"
                            style={{ minWidth: '100px' }}
                          >
                            Currency
                          </th>
                          <th
                            className="text-center py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '100px' }}
                            onClick={() => handleSort('status')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Status {getSortIcon('status')}
                            </div>
                          </th>
                          <th
                            className="text-center py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '80px' }}
                            onClick={() => handleSort('parts_count')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Parts {getSortIcon('parts_count')}
                            </div>
                          </th>
                          <th
                            className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50"
                            style={{ minWidth: '140px' }}
                            onClick={() => handleSort('created_at')}
                          >
                            <div className="flex items-center gap-1">
                              Created {getSortIcon('created_at')}
                            </div>
                          </th>
                          <th className="text-center py-3 px-4 font-medium" style={{ minWidth: '80px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedContracts.map((contract) => (
                          <tr
                            key={contract.id}
                            className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleRowClick(contract)}
                          >
                            <td className="py-3 px-4">
                              <span className="font-mono text-sm font-medium">{contract.contract_number}</span>
                            </td>
                            <td className="py-3 px-4 text-sm">{contract.customer_name || contract.customer_name || 'N/A'}</td>
                            <td className="py-3 px-4 text-sm">{contract.supplier_name || 'N/A'}</td>
                            <td className="py-3 px-4 text-sm">
                              {contract.valid_from ? formatDate(contract.valid_from) : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm">
                              {contract.valid_to ? formatDate(contract.valid_to) : '-'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                                {contract.currency}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {getStatusBadge(contract.status)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                                {contract.parts_count || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {formatDate(contract.created_at)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-xs text-primary hover:underline">View →</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
                    onPageChange={handlePageChange}
                    itemLabel="contracts"
                  />
                </>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedContractId && (
        <ContractPriceDetailModal
          contractId={selectedContractId}
          isOpen={showDetailModal}
          onClose={handleDetailModalClose}
          onEdit={isSupplier ? undefined : handleEdit}
          pricebookMode
          refreshTick={detailRefreshTick}
          onDeleted={fetchContracts}
          onSaved={fetchContracts}
        />
      )}

      {/* Add/Edit Modal */}
      {showAddEditModal && (
        <ContractPriceModal
          isOpen={showAddEditModal}
          onClose={handleModalClose}
          contract={editingContract}
          onSaved={() => setDetailRefreshTick(t => t + 1)}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ContractPriceImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
}
